/**
 * TopicArticles — one help topic: every current article of a category (US-6), by order then title. The
 * view behind a topic card on the help center and a topic row in the panel; the article rows open in
 * place in the panel (`onSelectArticle`) and navigate to /help/<id> on the page.
 */

import { ApiClientError } from '../../lib/api.core';
import { useHelpCategoryArticles } from '../../lib/api.help';
import { Skeleton } from '../ui/skeleton';
import { ArticleRow, HELP_HEADING_CLASS, HelpLoadError, RowsSkeleton } from './ArticleList';

interface TopicArticlesProps {
  category: string;
  onSelectArticle?: (articleId: string) => void;
  /**
   * The page gives the topic its own h1 (the label). In the panel the header already names the topic, so
   * no heading is rendered and only the article count is shown.
   */
  showHeading?: boolean;
}

function articleCountLabel(count: number): string {
  return `${count} ${count === 1 ? 'article' : 'articles'}`;
}

export function TopicArticles({ category, onSelectArticle, showHeading = false }: TopicArticlesProps) {
  const { data, isLoading, isError, error, refetch } = useHelpCategoryArticles(category);

  if (isLoading) {
    return (
      <div className="flex flex-col gap-3" data-testid="help-topic-loading">
        {showHeading && <Skeleton className="h-6 w-1/2" />}
        <RowsSkeleton data-testid="help-topic-loading-rows" />
      </div>
    );
  }

  if (isError || !data) {
    if (error instanceof ApiClientError && error.status === 404) {
      return (
        <div className="flex flex-col gap-2" data-testid="help-topic-not-found">
          <p className="text-sm font-semibold text-foreground">This topic isn't available</p>
          <p className="text-sm text-muted-foreground">It may have been moved or removed. Search the help center to find it.</p>
        </div>
      );
    }
    return (
      <HelpLoadError
        message="We couldn't load the articles in this topic."
        onRetry={() => void refetch()}
        data-testid="help-topic-error"
      />
    );
  }

  return (
    <section className="flex flex-col" data-testid="help-topic" aria-label={showHeading ? undefined : `${data.label} articles`}>
      {showHeading && (
        <h1 className={`${HELP_HEADING_CLASS} mb-1`} data-testid="help-topic-title">
          {data.label}
        </h1>
      )}
      <p className="mb-1 text-xs text-muted-foreground" data-testid="help-topic-count">
        {articleCountLabel(data.articles.length)}
      </p>
      <div className="flex flex-col">
        {data.articles.map((article) => (
          <ArticleRow
            key={article.id}
            articleId={article.id}
            title={article.title}
            excerpt={article.summary}
            onSelect={onSelectArticle}
            data-testid={`help-topic-article-${article.id}`}
          />
        ))}
      </div>
    </section>
  );
}
